import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import {
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { theme } from "../lib/theme";
import { useNetworkStore } from "../stores/network";
import {
  useBlockHistory,
  useBlockStats,
  useBlockConfig,
  useReorgs,
  useCurrentFees,
  useCheckpointStats,
} from "../api/hooks";
import { formatFJ } from "../lib/format";
import { noAnimation } from "../lib/chart";
import {
  PageContainer,
  PageTitle,
  Card,
  StatValue,
  StatLabel,
  Loading,
  PageHeader,
  ChartTitle,
} from "../components/ui";
import { RangeToolbar } from "../components/RangeToolbar";
import { LazyChart } from "../components/LazyChart";
import { ChartPicker, useChartPicker } from "../components/ChartPicker";
import { LiveToggle } from "../components/LiveToggle";
import { useChartCatchUp } from "../hooks/useChartCatchUp";

// All ranges are multiples of the Aztec epoch size (32 slots) so checkpoint
// boundaries land cleanly on the chart edges.
type TimeRange = "32" | "96" | "512" | "1024" | "5120";

// The counts alone; "blocks" is the toolbar's `unit`, which writes it once per button on a wide
// screen and leaves it off a phone.
const RANGE_LABELS: Record<TimeRange, string> = {
  "32": "32",
  "96": "96",
  "512": "512",
  "1024": "1K",
  "5120": "5K",
};

// ── Styled components (matching Fees page) ──

const MOBILE_HIDDEN_RANGES = new Set(["1024", "5120"]);

const TopBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

/** Live switch + range toolbar as one group, so they wrap together away from the page title. */
/**
 * Split a run of slots with no block into consecutive bands of one kind.
 *
 * A gap between two adjacent blocks can hold both kinds at once: the 2026-08-14 prune left 66 empty
 * slots of which the first 62 were mined-then-discarded and the last four failed on their own
 * account. One band per gap can only tell one of those stories, and telling the wrong one is how this
 * chart came to label three `checkpoint-valid` slots as pruned while the epoch timeline — applying
 * the same rule per slot — correctly showed them as missed.
 *
 * Positions interpolate by slot offset. The x-axis is block number and the gap spans exactly one
 * block step, so `offset / total` is the honest place to put a slot inside it.
 *
 * Exported for test: the boundary arithmetic is the part that silently mislabels. Runs are contiguous
 * in practice, so this returns one or two bands per gap, not sixty.
 */
export function splitSlotGapRuns({
  firstSlot,
  total,
  left,
  width,
  isOrphaned,
}: {
  firstSlot: number;
  total: number;
  left: number;
  width: number;
  isOrphaned: (slot: number) => boolean;
}): { x1: number; x2: number; slots: number; reorg: boolean; primary: boolean }[] {
  const runs: { x1: number; x2: number; slots: number; reorg: boolean; primary: boolean }[] = [];
  if (total <= 0) return runs;
  const at = (offset: number) => left + (offset / total) * width;

  let runStart = 0;
  let runKind = isOrphaned(firstSlot);
  for (let offset = 1; offset <= total; offset++) {
    // At `total` there is no slot to read, so flip deliberately: that flushes the final run without
    // needing the close duplicated after the loop.
    const kind = offset < total ? isOrphaned(firstSlot + offset) : !runKind;
    if (kind === runKind) continue;
    runs.push({
      x1: at(runStart),
      x2: at(offset),
      slots: offset - runStart,
      reorg: runKind,
      primary: false,
    });
    runStart = offset;
    runKind = kind;
  }
  // Only the longest run in a gap carries a label. A gap is at most one block step wide, so a short
  // run is a few pixels across and its label would sit on top of its neighbour's — which is what
  // "reorg −62" and "−4" did to each other. Labelling just the dominant run keeps the marker legible
  // at the zoom levels this chart is normally read at, and the legend counters still report both.
  let widest = 0;
  for (let i = 1; i < runs.length; i++) if (runs[i].slots > runs[widest].slots) widest = i;
  if (runs.length > 0) runs[widest].primary = true;
  return runs;
}

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  /* Lets the row narrow past its widest child so the range toolbar inside can scroll instead of
     handing this box a width the phone does not have. See RangeToolbar's Controls. */
  min-width: 0;
`;

const StatsColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.sm};
  flex: 1;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CompactStatCard = styled(Card)<{ mobileHidden?: boolean }>`
  padding: ${theme.spacing.sm};
  min-width: 0;
  text-align: center;
  @media (max-width: 768px) {
    ${(p) => p.mobileHidden && "display: none;"}
  }

  @media (max-width: 768px) {
    ${StatValue} {
      font-size: ${theme.fontSize.sm};
    }
    ${StatLabel} {
      font-size: 10px;
    }
  }
`;

const ChartCard = styled(Card)`
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const CpLegend = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xs};
  font-size: 10px;
  color: ${theme.colors.textMuted};
  flex-wrap: wrap;
`;

const CpLegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CpLegendDot = styled.span<{ opacity: number }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #6366f1;
  opacity: ${(p) => p.opacity};
`;

// Fixed YAxis width so all charts have identically positioned plot areas.
// Sized to fit the widest tick label across charts on prod data — formatFJ
// can emit "1.23M FJ" (~7 chars) and mana ticks like "150.0M". 56px gives
// a bit of slack vs ~50px theoretical so labels don't clip on dense data.
const Y_AXIS_WIDTH = 56;

function feeToUsd(raw: number, ethPerFeeAssetE12: string, ethUsdPrice: number): number {
  return (raw / 1e18) * (Number(ethPerFeeAssetE12) / 1e12) * ethUsdPrice;
}

function formatUsd(v: number): string {
  if (v >= 0.01) return `$${v.toFixed(4)}`;
  if (v >= 0.000001) return `$${v.toFixed(8)}`;
  return `$${v.toExponential(3)}`;
}

const MOBILE_CHARTS = [
  { key: "mana", label: "Mana" },
  { key: "txs", label: "Txs" },
  { key: "time", label: "Block time" },
  { key: "fees", label: "Fees" },
] as const;

export function Blocks() {
  const { selectedNetwork } = useNetworkStore();
  const catchUp = useChartCatchUp();
  const {
    selected: mobileChart,
    setSelected: setMobileChart,
    showChart,
  } = useChartPicker<(typeof MOBILE_CHARTS)[number]["key"]>("mana");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleChartClick = useCallback(
    (data: { activeLabel?: string }) => {
      if (data?.activeLabel) {
        navigate(`/${selectedNetwork}/block/${data.activeLabel}`);
      }
    },
    [selectedNetwork, navigate],
  );

  // Initialize from URL or defaults
  const urlFrom = searchParams.get("from") ? parseInt(searchParams.get("from")!, 10) : null;
  const urlTo = searchParams.get("to") ? parseInt(searchParams.get("to")!, 10) : null;
  const urlDiff = urlFrom != null && urlTo != null ? urlTo - urlFrom : null;

  const [range, setRange] = useState<TimeRange>(() => {
    if (urlDiff != null) {
      // Pick the smallest preset that covers the URL range
      if (urlDiff <= 32) return "32";
      if (urlDiff <= 96) return "96";
      if (urlDiff <= 512) return "512";
      if (urlDiff <= 1024) return "1024";
      return "5120";
    }
    return window.innerWidth <= 768 ? "32" : "96";
  });

  // Always use URL from/to as zoom so the exact range is shown
  const [zoomFrom, setZoomFrom] = useState<number | null>(urlFrom);
  const [zoomTo, setZoomTo] = useState<number | null>(urlTo);
  const [selecting, setSelecting] = useState<number | null>(null);
  const [selectEnd, setSelectEnd] = useState<number | null>(null);
  const selectingRef = useRef<number | null>(null);
  const selectEndRef = useRef<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type ChartMouseEvent = any;
  const getBlock = (e: ChartMouseEvent): number | null => {
    if (!e) return null;
    if (e.activeLabel !== undefined && e.activeLabel !== null) return Number(e.activeLabel);
    return e.activePayload?.[0]?.payload?.block ?? null;
  };
  const handleMouseDown = useCallback((e: ChartMouseEvent) => {
    const block = getBlock(e);
    if (block != null && !isNaN(block)) {
      selectingRef.current = block;
      selectEndRef.current = null;
      setSelecting(block);
      setSelectEnd(null);
    }
  }, []);
  const handleMouseMove = useCallback((e: ChartMouseEvent) => {
    if (selectingRef.current == null) return;
    const block = getBlock(e);
    if (block != null && !isNaN(block)) {
      selectEndRef.current = block;
      setSelectEnd(block);
    }
  }, []);
  const handleMouseUp = useCallback(() => {
    const s = selectingRef.current,
      end = selectEndRef.current;
    if (s != null && end != null && s !== end) {
      setZoomFrom(Math.min(s, end));
      setZoomTo(Math.max(s, end));
    }
    selectingRef.current = null;
    selectEndRef.current = null;
    setSelecting(null);
    setSelectEnd(null);
  }, []);
  const resetZoom = useCallback(() => {
    setZoomFrom(null);
    setZoomTo(null);
  }, []);
  const isZoomed = zoomFrom != null || zoomTo != null;

  const zoomProps = {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
  };
  const selectionOverlay =
    selecting != null && selectEnd != null ? (
      <ReferenceArea
        x1={Math.min(selecting, selectEnd)}
        x2={Math.max(selecting, selectEnd)}
        fill={theme.colors.primary}
        fillOpacity={0.15}
        stroke={theme.colors.primary}
        strokeOpacity={0.4}
        ifOverflow="extendDomain"
      />
    ) : null;

  const [offset, setOffset] = useState(0);
  const [offsetInitialized, setOffsetInitialized] = useState(urlTo == null);

  // Unscoped: just to get latest/earliest block for range computation
  const { data: globalStats } = useBlockStats(selectedNetwork);

  // Once we know the latest block, compute offset to cover the URL range
  const latestBlockGlobal = globalStats?.data?.blockRange.to ?? null;
  useEffect(() => {
    if (offsetInitialized || latestBlockGlobal == null || urlTo == null || urlFrom == null) return;
    const rangeSize = parseInt(range, 10);
    // Distance of the URL window from the current head, in pages. Round to the
    // nearest page so a URL window that ends within ±rangeSize/2 of the head
    // is treated as offset=0 (otherwise polling latency, where latest moves
    // forward by a few blocks between page load and globalStats arrival,
    // bumps offset by 1 and slides the window backwards by a full page).
    const distance = (latestBlockGlobal - urlTo) / rangeSize;
    const needed = Math.max(0, Math.round(distance));
    setOffset(needed);
    // If URL range matches a preset window, clear zoom so "Reset Zoom" doesn't show
    if (urlTo - urlFrom === rangeSize) {
      setZoomFrom(null);
      setZoomTo(null);
    }
    setOffsetInitialized(true);
  }, [latestBlockGlobal, offsetInitialized, urlTo, urlFrom, range]);
  const { data: configData } = useBlockConfig(selectedNetwork);
  const { data: currentFees } = useCurrentFees(selectedNetwork);

  const ethPrice = currentFees?.pricing?.ethUsdPrice ?? null;
  const ethPerFeeAssetE12 = currentFees?.pricing?.ethPerFeeAssetE12 ?? null;

  const latestBlock = latestBlockGlobal;
  const earliestBlock = globalStats?.data?.blockRange.from ?? 0;
  const rangeBlocks = parseInt(range, 10);
  const windowEnd = latestBlock != null ? latestBlock - offset * rangeBlocks : null;
  const fromBlock = windowEnd != null ? Math.max(0, windowEnd - rangeBlocks) : undefined;
  const toBlock = windowEnd ?? undefined;
  const canGoBack = isZoomed
    ? (zoomFrom ?? 0) > earliestBlock
    : fromBlock != null && fromBlock > earliestBlock;
  const canGoForward = isZoomed ? (zoomTo ?? 0) < (latestBlock ?? 0) : offset > 0;

  const goBack = () => {
    if (!canGoBack) return;
    if (isZoomed && zoomFrom != null && zoomTo != null) {
      const span = zoomTo - zoomFrom;
      setZoomFrom(Math.max(earliestBlock, zoomFrom - span));
      setZoomTo(Math.max(earliestBlock + span, zoomTo - span));
    } else {
      setOffset((o) => o + 1);
    }
  };
  const goForward = () => {
    if (!canGoForward) return;
    if (isZoomed && zoomFrom != null && zoomTo != null) {
      const span = zoomTo - zoomFrom;
      setZoomFrom(zoomFrom + span);
      setZoomTo(zoomTo + span);
    } else {
      setOffset((o) => o - 1);
    }
  };
  // "Latest" refetches as well as resetting the window: while the charts are paused the head this
  // page knows about is itself behind the chain, so clearing offset and zoom alone would land on a
  // stale idea of "latest".
  const goToLatest = () => {
    setOffset(0);
    resetZoom();
    catchUp();
  };

  // Scoped to the visible range (zoom overrides range selector)
  const effectiveFrom = zoomFrom ?? fromBlock;
  const effectiveTo = zoomTo ?? toBlock;

  // Sync URL with visible range + network
  useEffect(() => {
    if (effectiveFrom != null && effectiveTo != null) {
      const next = new URLSearchParams(searchParams);
      next.set("from", String(effectiveFrom));
      next.set("to", String(effectiveTo));
      setSearchParams(next, { replace: true });
    }
  }, [effectiveFrom, effectiveTo, selectedNetwork, setSearchParams]);
  const rangeOpts = useMemo(
    () => ({ from: effectiveFrom, to: effectiveTo }),
    [effectiveFrom, effectiveTo],
  );
  const cpRangeOpts = useMemo(
    () => ({ fromBlock: effectiveFrom, toBlock: effectiveTo }),
    [effectiveFrom, effectiveTo],
  );
  const { data: statsData, isLoading: statsLoading } = useBlockStats(
    effectiveFrom != null ? selectedNetwork : "",
    rangeOpts,
  );
  const { data: cpStatsData } = useCheckpointStats(
    effectiveFrom != null ? selectedNetwork : "",
    cpRangeOpts,
  );

  // Fetch data covering the wider of range selector or zoom
  const fetchFrom =
    zoomFrom != null && fromBlock != null ? Math.min(zoomFrom, fromBlock) : (fromBlock ?? zoomFrom);
  const fetchTo =
    zoomTo != null && toBlock != null ? Math.max(zoomTo, toBlock) : (toBlock ?? zoomTo);
  const historyOpts = useMemo(
    () => ({
      from: fetchFrom ?? undefined,
      to: fetchTo ?? undefined,
      limit: Math.max(rangeBlocks, (fetchTo ?? 0) - (fetchFrom ?? 0)) + 10,
    }),
    [fetchFrom, fetchTo, rangeBlocks],
  );
  const rangeReady = fromBlock != null;
  const { data: historyData, isLoading: historyLoading } = useBlockHistory(
    rangeReady ? selectedNetwork : "",
    historyOpts,
  );

  const config = configData?.data;
  const stats = statsData?.data;
  const epochDuration = config?.aztecEpochDuration ?? null;

  // Slot window of the loaded history, so the request asks only about what is on screen. Unscoped
  // when history hasn't arrived yet — the list is tiny either way.
  const historySlots = useMemo(() => {
    const slots = (historyData?.data ?? [])
      .map((b) => b.slotNumber)
      .filter((s): s is number => s != null);
    return slots.length > 0
      ? { fromSlot: Math.min(...slots), toSlot: Math.max(...slots) }
      : undefined;
  }, [historyData]);
  const { data: reorgData } = useReorgs(selectedNetwork, historySlots);
  // The individual discarded slots, not the prune's span. A span is not uniformly orphaned — on
  // 2026-08-14 it held 62 mined-then-discarded slots and four that failed on their own account — and
  // attributing the whole span to the prune made this chart contradict the epoch timeline about those
  // four. The server applies `isOrphanedSlot` and sends the result.
  const orphanedSlotSet = useMemo(
    () => new Set(reorgData?.orphanedSlots ?? []),
    [reorgData],
  );

  const { chartData, checkpointBands, missedSlotGaps, epochBoundaries } = useMemo(() => {
    if (!historyData?.data)
      return { chartData: [], checkpointBands: [], missedSlotGaps: [], epochBoundaries: [] };
    const all = historyData.data.map((b, i, arr) => ({
      block: b.blockNumber,
      slotNumber: b.slotNumber,
      numTxs: b.numTxs,
      manaUsed: b.totalManaUsed ? Number(b.totalManaUsed) : 0,
      totalFees: b.totalFees ? Number(b.totalFees) : 0,
      blockTime: (() => {
        if (i === 0 || !b.timestamp || !arr[i - 1].timestamp) return null;
        if (b.blockNumber - arr[i - 1].blockNumber !== 1) return null;
        return b.timestamp - arr[i - 1].timestamp!;
      })(),
      checkpointNumber: b.checkpointNumber,
      cpStatus: b.cpStatus,
      pricing: b.pricing ?? null,
    }));
    const filtered =
      zoomFrom != null && zoomTo != null
        ? all.filter((d) => d.block >= zoomFrom && d.block <= zoomTo)
        : all;

    // Slot gaps: consecutive blocks whose slotNumber jumps by >1 means the intervening slots did
    // not land on L1. Render a band between the two bars whose width is proportional to the count.
    //
    // Two different things produce that shape, and until now both were labelled "missed slot". A
    // run of ordinary misses really is proposer failure. A rollup prune is not: the chain wound its
    // pending tip back to the last proven checkpoint and deleted everything after it, so the blocks
    // that filled those slots were indexed and then removed. That is why block and checkpoint
    // numbers run contiguously across the hole — `curr.block - prev.block === 1` holds — and why
    // the largest gap this chart has ever drawn, 66 slots on 2026-08-14, read as a routine miss.
    //
    // Each gap is split into consecutive runs of one kind, and one band is drawn per run rather than
    // per gap. The two kinds really are interleaved — a prune's 66 slots on 2026-08-14 were 62
    // discarded followed by four genuine misses — and one band per gap could only ever tell one of
    // those two stories. Runs are contiguous in practice (the discarded stretch, then the tail that
    // produced nothing), so this is a band or two per gap, not sixty.
    //
    // Positions interpolate across the gap by slot offset. The x-axis is block number and the gap
    // spans exactly one block step, so offset/total is the honest place to put a slot inside it.
    const gaps: {
      x1: number;
      x2: number;
      slots: number;
      reorg: boolean;
      primary: boolean;
    }[] = [];
    for (let i = 1; i < all.length; i++) {
      const prev = all[i - 1];
      const curr = all[i];
      if (curr.block - prev.block !== 1) continue;
      if (prev.slotNumber == null || curr.slotNumber == null) continue;
      const total = curr.slotNumber - prev.slotNumber - 1;
      if (total <= 0) continue;
      if (zoomFrom != null && zoomTo != null && (curr.block < zoomFrom || prev.block > zoomTo))
        continue;

      gaps.push(
        ...splitSlotGapRuns({
          firstSlot: prev.slotNumber + 1,
          total,
          left: prev.block + 0.05,
          width: curr.block - 0.05 - (prev.block + 0.05),
          isOrphaned: (slot) => orphanedSlotSet.has(slot),
        }),
      );
    }

    // Compute checkpoint bands from block data
    const bands: {
      cpNumber: number;
      x1: number;
      x2: number;
      status: string;
      blockCount: number;
      attestations: number | null;
      totalMana: number;
    }[] = [];
    const cpMap = new Map<
      number,
      {
        minBlock: number;
        maxBlock: number;
        status: string;
        blockCount: number;
        attestations: number | null;
        totalMana: number;
      }
    >();
    for (const b of historyData.data) {
      if (b.checkpointNumber == null) continue;
      const mana = b.totalManaUsed ? Number(b.totalManaUsed) : 0;
      const existing = cpMap.get(b.checkpointNumber);
      if (existing) {
        existing.minBlock = Math.min(existing.minBlock, b.blockNumber);
        existing.maxBlock = Math.max(existing.maxBlock, b.blockNumber);
        existing.totalMana += mana;
        existing.blockCount++;
      } else {
        cpMap.set(b.checkpointNumber, {
          minBlock: b.blockNumber,
          maxBlock: b.blockNumber,
          status: b.cpStatus ?? "checkpointed",
          blockCount: 1,
          attestations: b.cpAttestations,
          totalMana: mana,
        });
      }
    }
    for (const [cpNum, cp] of [...cpMap.entries()].sort((a, b) => a[0] - b[0])) {
      if (zoomFrom != null && zoomTo != null && (cp.maxBlock < zoomFrom || cp.minBlock > zoomTo))
        continue;
      bands.push({
        cpNumber: cpNum,
        x1: cp.minBlock - 0.5,
        x2: cp.maxBlock + 0.5,
        status: cp.status,
        blockCount: cp.blockCount,
        attestations: cp.attestations,
        totalMana: cp.totalMana,
      });
    }

    // Epoch boundaries: detect transitions between consecutive blocks where
    // floor(slot/epochDuration) increments. The boundary in block-space is
    // anchored at `curr.block - 0.5` (left edge of the first block of the new
    // epoch). If slot 0 of the epoch was missed, the anchor is approximate —
    // it lands at the first OBSERVED block of the new epoch, which is what a
    // user visually associates with "epoch starts here."
    const boundaries: { x: number; epoch: number }[] = [];
    if (epochDuration && epochDuration > 0) {
      let prevEpoch: number | null = null;
      for (const d of all) {
        if (d.slotNumber == null) continue;
        const epoch = Math.floor(d.slotNumber / epochDuration);
        if (prevEpoch !== null && epoch !== prevEpoch) {
          if (zoomFrom == null || zoomTo == null || (d.block >= zoomFrom && d.block <= zoomTo)) {
            boundaries.push({ x: d.block - 0.5, epoch });
          }
        }
        prevEpoch = epoch;
      }
    }

    return {
      chartData: filtered,
      checkpointBands: bands,
      missedSlotGaps: gaps,
      epochBoundaries: boundaries,
    };
  }, [historyData, zoomFrom, zoomTo, epochDuration, orphanedSlotSet]);

  const maxMana = config?.maxL2BlockGas ? Number(config.maxL2BlockGas) : null;
  const cpStats = cpStatsData?.data;

  // Checkpoint band overlays — generate per chart with context-specific labels
  // Only show labels when zoomed in enough to read them
  const showCpLabels = chartData.length <= 150;
  type CpChartType = "mana" | "txs" | "blockTime" | "fees";
  const makeCpBands = (chartType: CpChartType) =>
    checkpointBands.map((band, i) => {
      const fillOpacity =
        band.status === "finalized" ? 0.18 : band.status === "proven" ? 0.12 : 0.07;
      const fill = i % 2 === 0 ? "#6366f1" : "#a78bfa";
      let label: string | undefined;
      if (showCpLabels && band.blockCount > 1) {
        switch (chartType) {
          case "mana":
            label = band.totalMana > 0 ? `${(band.totalMana / 1e6).toFixed(1)}M mana` : undefined;
            break;
          case "txs": {
            // Sum txs from chartData for blocks in this checkpoint
            const txs = chartData
              .filter((d) => d.block >= band.x1 && d.block <= band.x2)
              .reduce((s, d) => s + d.numTxs, 0);
            label = `${txs} txs`;
            break;
          }
          case "blockTime":
            label = `${band.blockCount} blocks`;
            break;
          case "fees": {
            const fees = chartData
              .filter((d) => d.block >= band.x1 && d.block <= band.x2)
              .reduce((s, d) => s + d.totalFees, 0);
            label = fees > 0 ? formatFJ(fees) : undefined;
            break;
          }
        }
      }
      return (
        <ReferenceArea
          key={`cp-${band.cpNumber}`}
          x1={band.x1}
          x2={band.x2}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={fill}
          strokeOpacity={0.2}
          ifOverflow="extendDomain"
          label={
            label
              ? (props: { viewBox?: { x?: number; y?: number; width?: number } }) => {
                  const vx = props.viewBox?.x ?? 0;
                  const vw = props.viewBox?.width ?? 0;
                  const vy = props.viewBox?.y ?? 0;
                  const cx = vx + vw / 2;
                  return (
                    <text
                      x={0}
                      y={0}
                      transform={`translate(${cx}, ${vy - 26}) rotate(-55)`}
                      fontSize={9}
                      fill={theme.colors.text}
                      opacity={0.6}
                      fontFamily="monospace"
                      textAnchor="middle"
                    >
                      {label}
                    </text>
                  );
                }
              : undefined
          }
        />
      );
    });

  // Split, not summed: a prune's slots are not proposer misses and adding them into one figure is
  // how 66 discarded slots came to be reported as missed ones.
  const missedSlots = missedSlotGaps.reduce((s, g) => (g.reorg ? s : s + g.slots), 0);
  const discardedSlots = missedSlotGaps.reduce((s, g) => (g.reorg ? s + g.slots : s), 0);

  // Shared x-axis domain + ticks across all charts. Domain is padded by 0.5 so
  // half-width bars and checkpoint bands at the edges don't spill past the plot
  // area; ticks are explicit integers so labels don't snap to ".5" values
  // produced by the padded domain.
  const { xDomain, xTicks } = useMemo<{ xDomain: [number, number]; xTicks: number[] }>(() => {
    if (chartData.length === 0) return { xDomain: [0, 0], xTicks: [] };
    const min = chartData[0].block;
    const max = chartData[chartData.length - 1].block;
    const span = Math.max(1, max - min);
    // ~6 ticks across the visible range, snapped to a nice integer step.
    // Floor at 1 because block numbers are integers — fractional ticks would
    // render as e.g. "#71570.4" which is meaningless.
    const rough = Math.max(1, span / 6);
    const pow = Math.pow(10, Math.floor(Math.log10(rough)));
    const nice = [1, 2, 5, 10].find((m) => m * pow >= rough) ?? 10;
    const step = Math.max(1, nice * pow);
    const start = Math.ceil(min / step) * step;
    const ticks: number[] = [];
    for (let v = start; v <= max; v += step) ticks.push(v);
    return { xDomain: [min - 0.5, max + 0.5], xTicks: ticks };
  }, [chartData]);
  const showMissedLabels = chartData.length <= 150;
  // Skip rendering individual gap markers when they'd flood the DOM (each
  // marker is a ReferenceArea, multiplied across 4 charts). 1500 is enough
  // headroom for prod-mainnet 5000-block windows (~600 gaps) while still
  // catching pathological cases on networks with extreme missed-slot density.
  // The legend's in-view counter still surfaces the total when over the cap.
  const MAX_MISSED_MARKERS = 1500;
  const renderMissedMarkers =
    missedSlotGaps.length > 0 && missedSlotGaps.length <= MAX_MISSED_MARKERS;
  const makeMissedSlotMarkers = () =>
    !renderMissedMarkers
      ? null
      : missedSlotGaps.map((gap) => {
      // A prune's band takes the orphan slate and the word, because the number alone was the whole
      // problem: 66 slots vanished and the chart said "–66" in the same colour it uses for a
      // proposer missing one. The colour is outside the warning/danger ramp for the same reason it
      // is on the epoch timeline — nobody failed here.
      const color = gap.reorg ? theme.colors.orphaned : theme.colors.warning;
      return (
      <ReferenceArea
        key={`miss-${gap.x1}`}
        x1={gap.x1}
        x2={gap.x2}
        fill={color}
        fillOpacity={0.22}
        stroke={color}
        strokeOpacity={0.5}
        strokeDasharray="2 2"
        ifOverflow="extendDomain"
        label={
          showMissedLabels && gap.primary && gap.slots > 1
            ? (props: { viewBox?: { x?: number; y?: number; width?: number; height?: number } }) => {
                const vx = props.viewBox?.x ?? 0;
                const vw = props.viewBox?.width ?? 0;
                const vy = props.viewBox?.y ?? 0;
                const vh = props.viewBox?.height ?? 0;
                return (
                  <text
                    x={vx + vw / 2}
                    y={vy + vh / 2}
                    fontSize={9}
                    fill={color}
                    fontFamily="monospace"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {/* "reorg" only when it fits: the band is as wide as the gap, and a two-slot
                        prune has no room for a word. The colour still distinguishes it. */}
                    {gap.reorg && vw > 42 ? `reorg –${gap.slots}` : `–${gap.slots}`}
                  </text>
                );
              }
            : undefined
        }
      />
    );});

  // Epoch boundary markers — thin vertical line at the start of each new epoch.
  // Cap the count for the same DOM-flood reason as missed-slot markers; on a
  // 5120-block window (~160 epochs) we'd otherwise add 160 lines × 4 charts.
  const MAX_EPOCH_LINES = 200;
  const renderEpochBoundaries =
    epochBoundaries.length > 0 && epochBoundaries.length <= MAX_EPOCH_LINES;
  const showEpochLabels = chartData.length <= 300;
  const makeEpochBoundaries = () =>
    !renderEpochBoundaries
      ? null
      : epochBoundaries.map((b) => (
          <ReferenceLine
            key={`epoch-${b.epoch}`}
            x={b.x}
            stroke={theme.colors.textMuted}
            strokeDasharray="2 4"
            strokeOpacity={0.6}
            ifOverflow="extendDomain"
            label={
              showEpochLabels
                ? (props: { viewBox?: { x?: number; y?: number } }) => {
                    const lx = props.viewBox?.x ?? 0;
                    const ly = props.viewBox?.y ?? 0;
                    // Anchor above the plot area (the chart `margin.top` is 55,
                    // so ly is the top of the plot — drop the label a few px
                    // above that, rotated so it doesn't overlap neighbors).
                    return (
                      <text
                        x={0}
                        y={0}
                        transform={`translate(${lx}, ${ly - 6}) rotate(-55)`}
                        fontSize={9}
                        fill={theme.colors.textMuted}
                        fontFamily="monospace"
                        textAnchor="start"
                      >
                        {`e${b.epoch}`}
                      </text>
                    );
                  }
                : undefined
            }
          />
        ));

  const tooltipStyle = {
    background: theme.colors.bgCard,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.sm,
    fontSize: 11,
    fontFamily: "monospace",
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Block Analytics</PageTitle>
        <HeaderControls>
          <LiveToggle knownHead={latestBlock} />
          <RangeToolbar
            unit="blocks"
            ranges={Object.entries(RANGE_LABELS).map(([key, label]) => ({
              key,
              label,
              mobileHidden: MOBILE_HIDDEN_RANGES.has(key),
            }))}
            activeRange={(() => {
              const zoomSize =
                isZoomed && zoomFrom != null && zoomTo != null ? zoomTo - zoomFrom : null;
              if (zoomSize != null) {
                const match = Object.keys(RANGE_LABELS).find((r) => parseInt(r, 10) === zoomSize);
                return match ?? null;
              }
              return range;
            })()}
            onRangeSelect={(r) => {
              const newSize = parseInt(r, 10);
              if (isZoomed && zoomFrom != null && zoomTo != null) {
                const center = Math.round((zoomFrom + zoomTo) / 2);
                const newFrom = Math.max(0, center - Math.floor(newSize / 2));
                setZoomFrom(newFrom);
                setZoomTo(newFrom + newSize);
              } else {
                resetZoom();
                setOffset(0);
              }
              setRange(r as TimeRange);
            }}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            onGoBack={goBack}
            onGoForward={goForward}
            onGoToLatest={goToLatest}
          />
        </HeaderControls>
      </PageHeader>

      {statsLoading && <Loading />}

      {stats && (
        <>
          {/* Top bar: 2-row stats grid + sequencer pie */}
          <TopBar>
            <StatsColumn>
              <CompactStatCard>
                <StatLabel>Avg Block Time</StatLabel>
                <StatValue>{stats.avgBlockTime}s</StatValue>
              </CompactStatCard>
              <CompactStatCard>
                <StatLabel>Avg Txs/Block</StatLabel>
                <StatValue>{stats.avgTxsPerBlock}</StatValue>
              </CompactStatCard>
              <CompactStatCard>
                <StatLabel>Empty Blocks</StatLabel>
                <StatValue>{stats.emptyBlockPct}%</StatValue>
              </CompactStatCard>
              <CompactStatCard>
                <StatLabel>Avg Mana/Block</StatLabel>
                <StatValue>
                  {stats.avgManaPerBlock
                    ? `${(Number(stats.avgManaPerBlock) / 1e6).toFixed(2)}M`
                    : "-"}
                </StatValue>
              </CompactStatCard>
              <CompactStatCard>
                <StatLabel>Avg Fees/Block</StatLabel>
                <StatValue>
                  {stats.avgFeesPerBlock ? formatFJ(stats.avgFeesPerBlock) : "-"}
                </StatValue>
                {stats.avgFeesPerBlock && ethPrice != null && ethPerFeeAssetE12 != null && (
                  <div
                    style={{
                      fontSize: 9,
                      color: theme.colors.textMuted,
                      marginTop: 1,
                    }}
                  >
                    {formatUsd(
                      feeToUsd(Number(stats.avgFeesPerBlock), ethPerFeeAssetE12, ethPrice),
                    )}
                  </div>
                )}
              </CompactStatCard>
              {maxMana && (
                <CompactStatCard>
                  <StatLabel>Max Mana/Block</StatLabel>
                  <StatValue>{(maxMana / 1e6).toFixed(1)}M</StatValue>
                </CompactStatCard>
              )}
              {cpStats && (
                <>
                  <CompactStatCard mobileHidden>
                    <StatLabel>Checkpoints</StatLabel>
                    <StatValue>{cpStats.checkpointCount}</StatValue>
                  </CompactStatCard>
                  <CompactStatCard mobileHidden>
                    <StatLabel>Blocks/Checkpoint</StatLabel>
                    <StatValue>{cpStats.avgBlocksPerCheckpoint}</StatValue>
                  </CompactStatCard>
                  <CompactStatCard mobileHidden>
                    <StatLabel>Avg Mana/Checkpoint</StatLabel>
                    <StatValue>
                      {cpStats.avgManaPerCheckpoint
                        ? `${(Number(cpStats.avgManaPerCheckpoint) / 1e6).toFixed(2)}M`
                        : "-"}
                    </StatValue>
                  </CompactStatCard>
                </>
              )}
            </StatsColumn>

          </TopBar>

          <ChartPicker charts={MOBILE_CHARTS} value={mobileChart} onChange={setMobileChart} />

          {/* Block Utilization */}
          {showChart("mana") && (
            <LazyChart height={250}>
              <ChartCard>
                <ChartTitle>Block Utilization (L2 Mana)</ChartTitle>
                {historyLoading ? (
                  <Loading />
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 55, right: 10, bottom: 0, left: 10 }}
                      {...zoomProps}
                      onClick={handleChartClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.3} />
                      {makeCpBands("mana")}
                      {makeMissedSlotMarkers()}
                      {makeEpochBoundaries()}
                      <XAxis
                        dataKey="block"
                        type="number"
                        domain={xDomain}
                        ticks={xTicks}
                        allowDataOverflow={false}
                        stroke={theme.colors.textMuted}
                        fontSize={10}
                        tickFormatter={(v) => `#${v}`}
                      />
                      <YAxis
                        width={Y_AXIS_WIDTH}
                        stroke={theme.colors.textMuted}
                        fontSize={10}
                        tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number) => [
                          `${(value / 1e6).toFixed(2)}M mana${maxMana ? ` (${((value / maxMana) * 100).toFixed(1)}%)` : ""}`,
                          "Mana Used",
                        ]}
                        labelFormatter={(v, payload) => {
                          const d = payload?.[0]?.payload;
                          const cp = d?.checkpointNumber;
                          const status = d?.cpStatus;
                          if (cp == null) return `Block #${v}`;
                          return `Block #${v} ${status ? ` (${status})` : ""} · Checkpoint #${cp}`;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="manaUsed"
                        stroke={theme.colors.primary}
                        fill={theme.colors.primary}
                        fillOpacity={0.15}
                        {...noAnimation}
                      />
                      {maxMana && (
                        <ReferenceLine
                          y={maxMana}
                          stroke={theme.colors.danger}
                          strokeDasharray="3 3"
                          label={{
                            value: "Max",
                            position: "right",
                            fontSize: 10,
                            fill: theme.colors.danger,
                          }}
                        />
                      )}
                      {selectionOverlay}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </LazyChart>
          )}


          {/* Transactions Per Block */}
          {showChart("txs") && (
            <LazyChart height={200}>
              <ChartCard>
                <ChartTitle>Transactions Per Block</ChartTitle>
                {historyLoading ? (
                  <Loading />
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 55, right: 10, bottom: 0, left: 10 }}
                      {...zoomProps}
                      onClick={handleChartClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.3} />
                      {makeCpBands("txs")}
                      {makeMissedSlotMarkers()}
                      {makeEpochBoundaries()}
                      <XAxis
                        dataKey="block"
                        type="number"
                        domain={xDomain}
                        ticks={xTicks}
                        allowDataOverflow={false}
                        stroke={theme.colors.textMuted}
                        fontSize={10}
                        tickFormatter={(v) => `#${v}`}
                      />
                      <YAxis width={Y_AXIS_WIDTH} stroke={theme.colors.textMuted} fontSize={10} />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number) => [`${value}`, "Txs"]}
                        labelFormatter={(v, payload) => {
                          const d = payload?.[0]?.payload;
                          const cp = d?.checkpointNumber;
                          const status = d?.cpStatus;
                          if (cp == null) return `Block #${v}`;
                          return `Block #${v} ${status ? ` (${status})` : ""} · Checkpoint #${cp}`;
                        }}
                      />
                      <Bar
                        dataKey="numTxs"
                        fill={theme.colors.accent}
                        fillOpacity={0.4}
                        stroke={theme.colors.accent}
                        strokeOpacity={0.6}
                        strokeWidth={0.5}
                        {...noAnimation}
                      />
                      {selectionOverlay}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </LazyChart>
          )}


          {/* Block Time */}
          {showChart("time") && (
            <LazyChart height={200}>
              <ChartCard>
                <ChartTitle>Block Time</ChartTitle>
                {historyLoading ? (
                  <Loading />
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 55, right: 10, bottom: 0, left: 10 }}
                      {...zoomProps}
                      onClick={handleChartClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.3} />
                      {makeCpBands("blockTime")}
                      {makeMissedSlotMarkers()}
                      {makeEpochBoundaries()}
                      <XAxis
                        dataKey="block"
                        type="number"
                        domain={xDomain}
                        ticks={xTicks}
                        allowDataOverflow={false}
                        stroke={theme.colors.textMuted}
                        fontSize={10}
                        tickFormatter={(v) => `#${v}`}
                      />
                      <YAxis width={Y_AXIS_WIDTH} stroke={theme.colors.textMuted} fontSize={10} unit="s" />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number) => [`${value}s`, "Block Time"]}
                        labelFormatter={(v, payload) => {
                          const d = payload?.[0]?.payload;
                          const cp = d?.checkpointNumber;
                          const status = d?.cpStatus;
                          if (cp == null) return `Block #${v}`;
                          return `Block #${v} ${status ? ` (${status})` : ""} · Checkpoint #${cp}`;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="blockTime"
                        stroke={theme.colors.warning}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls
                        {...noAnimation}
                      />
                      {config?.aztecSlotDuration && (
                        <ReferenceLine
                          y={config.aztecSlotDuration}
                          stroke={theme.colors.success}
                          strokeDasharray="3 3"
                          label={{
                            value: "Target",
                            position: "right",
                            fontSize: 10,
                            fill: theme.colors.success,
                          }}
                        />
                      )}
                      {selectionOverlay}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </LazyChart>
          )}


          {/* Fee Revenue */}
          {showChart("fees") && (
            <LazyChart height={200}>
              <ChartCard>
                <ChartTitle>Fee Revenue Per Block</ChartTitle>
                {historyLoading ? (
                  <Loading />
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 55, right: 10, bottom: 0, left: 10 }}
                      {...zoomProps}
                      onClick={handleChartClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.3} />
                      {makeCpBands("fees")}
                      {makeMissedSlotMarkers()}
                      {makeEpochBoundaries()}
                      <XAxis
                        dataKey="block"
                        type="number"
                        domain={xDomain}
                        ticks={xTicks}
                        allowDataOverflow={false}
                        stroke={theme.colors.textMuted}
                        fontSize={10}
                        tickFormatter={(v) => `#${v}`}
                      />
                      <YAxis
                        width={Y_AXIS_WIDTH}
                        stroke={theme.colors.textMuted}
                        fontSize={10}
                        tickFormatter={(v) => formatFJ(v)}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value, _name, item) => {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const p = (item as any)?.payload?.pricing as { ethUsdPrice: number; ethPerFeeAssetE12: string; snapshotAgeSeconds: number } | null | undefined;
                          const fj = formatFJ(Number(value));
                          const ep = p?.ethPerFeeAssetE12 ?? ethPerFeeAssetE12;
                          const eusd = p?.ethUsdPrice ?? ethPrice;
                          const usd =
                            ep != null && eusd != null
                              ? ` (${formatUsd(feeToUsd(Number(value), ep, eusd))})`
                              : "";
                          return [`${fj}${usd}`, "Fees"];
                        }}
                        labelFormatter={(v, payload) => {
                          const d = payload?.[0]?.payload;
                          const cp = d?.checkpointNumber;
                          const status = d?.cpStatus;
                          const stale =
                            d?.pricing != null && d.pricing.snapshotAgeSeconds > 10800
                              ? ` ⚠ stale pricing (${Math.round(d.pricing.snapshotAgeSeconds / 3600)}h old)`
                              : "";
                          const label = cp == null
                            ? `Block #${v}`
                            : `Block #${v}${status ? ` (${status})` : ""} · Checkpoint #${cp}`;
                          return `${label}${stale}`;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="totalFees"
                        stroke={theme.colors.success}
                        fill={theme.colors.success}
                        fillOpacity={0.1}
                        {...noAnimation}
                      />
                      {selectionOverlay}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </LazyChart>
          )}


          {/* Checkpoint legend for chart bands */}
          {(checkpointBands.length > 0 || missedSlotGaps.length > 0 || epochBoundaries.length > 0) && (
            <CpLegend style={{ marginBottom: theme.spacing.sm }}>
              <CpLegendItem>
                <CpLegendDot opacity={0.07} /> Checkpointed
              </CpLegendItem>
              <CpLegendItem>
                <CpLegendDot opacity={0.12} /> Proven
              </CpLegendItem>
              <CpLegendItem>
                <CpLegendDot opacity={0.18} /> Finalized
              </CpLegendItem>
              {epochBoundaries.length > 0 && (
                <CpLegendItem title="Vertical line at the start of each new epoch">
                  <span
                    style={{
                      width: 0,
                      height: 10,
                      borderLeft: `1px dashed ${theme.colors.textMuted}`,
                      marginRight: 2,
                    }}
                  />
                  Epoch start
                </CpLegendItem>
              )}
              {/* Two entries, because the bands mean two different things and used to share one
                  name. Each appears only when that kind is actually on screen. */}
              {missedSlotGaps.some((g) => !g.reorg) && (
                <CpLegendItem
                  title="Slots between adjacent blocks where no checkpoint landed on L1"
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: theme.colors.warning,
                      opacity: 0.5,
                      border: `1px dashed ${theme.colors.warning}`,
                    }}
                  />{" "}
                  Missed slot
                </CpLegendItem>
              )}
              {missedSlotGaps.some((g) => g.reorg) && (
                <CpLegendItem
                  title={
                    "Slots discarded by a rollup prune: these blocks were produced and published " +
                    "to L1, then removed when the pending chain was wound back to the last proven " +
                    "checkpoint. Block and checkpoint numbering continues across the gap because " +
                    "the chain reuses the numbers."
                  }
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: theme.colors.orphaned,
                      opacity: 0.5,
                      border: `1px dashed ${theme.colors.orphaned}`,
                    }}
                  />{" "}
                  Discarded
                </CpLegendItem>
              )}
              <span style={{ color: theme.colors.textMuted, fontSize: 10 }}>
                ({checkpointBands.length} checkpoints
                {epochBoundaries.length > 0
                  ? `, ${epochBoundaries.length} epoch boundar${epochBoundaries.length === 1 ? "y" : "ies"}${
                      !renderEpochBoundaries ? " — overlay hidden" : ""
                    }`
                  : ""}
                {/* Counted apart from each other. Reporting "66 missed slots" for a window whose
                    only gap was a prune is the exact sentence this whole change exists to stop —
                    and with both kinds present the line has two figures to fit, so "slots" goes.
                    The legend swatches directly to the left already say what each one is. */}
                {missedSlots > 0 ? `, ${missedSlots} missed` : ""}
                {discardedSlots > 0 ? `, ${discardedSlots} discarded` : ""}
                {missedSlotGaps.length > 0 && !renderMissedMarkers ? " — overlay hidden" : ""}{" "}
                in view)
              </span>
            </CpLegend>
          )}
        </>
      )}
    </PageContainer>
  );
}
